import { SafeResourceUrl } from '@angular/platform-browser';
import * as _ from 'lodash';
import { Model } from '../classes/model';
import { ModelHelper } from '../classes/model-helper';
import { CompanyType } from '../enums/company-types.enum';
import { UserRole } from '../enums/user-role.enum';
import { UserState } from '../enums/user-state.enum';
import { WorkflowToken } from '../enums/workflow-token.enum';
import { Company } from './company.model';
import { Role } from './role.model';
import { SignatureData } from './signature-data.model';

/**
 * User model
 */
export class User extends Model {
	uid: string;
	externalUid: string;
	username: string;
	userState: UserState;
	title: string;
	gender: string;
	firstName: string;
	lastName: string;
	phoneNumber: string;
	emailAddress: string;
	skypeName: string;
	roles: Role[];
	company: Company;
	language: string;
	timezone: string;
	listrange: string;
	password: string;
	signatureData: SignatureData;
	signatureLink: SafeResourceUrl;
	avatarLink: SafeResourceUrl;
	// Indicates when the user accepted the Terms and Conditions. If this date is older than the announcement date of the new
	// Terms the user Needs to reaccept them.
	acceptedTermsAndConditions: Date;

	/**
	 * Non static mapping function
	 *
	 * @param data
	 * @return User
	 */
	public map(data: object): this {
		ModelHelper.map(data, this, 'uid');
		ModelHelper.map(data, this, 'externalUid');
		ModelHelper.map(data, this, 'username');
		ModelHelper.map(data, this, 'userState');
		ModelHelper.map(data, this, 'title');
		ModelHelper.map(data, this, 'gender');
		ModelHelper.map(data, this, 'firstName');
		ModelHelper.map(data, this, 'lastName');
		ModelHelper.map(data, this, 'phoneNumber');
		ModelHelper.map(data, this, 'emailAddress');
		ModelHelper.map(data, this, 'skypeName');
		ModelHelper.map(data, this, 'avatarLink');
		ModelHelper.map(data, this, 'timezone');
		ModelHelper.map(data, this, 'roles', Role);
		ModelHelper.map(data, this, 'company', Company);
		ModelHelper.map(data, this, 'listrange');
		ModelHelper.map(data, this, 'password');
		ModelHelper.map(data, this, 'signatureData', SignatureData);
		ModelHelper.map(data, this, 'signatureLink');
		ModelHelper.map(data, this, 'acceptedTermsAndConditions');

		// Use plain language code
		if (data && data['language'] !== undefined) {
			this.language = _.get(data, 'language.code', data['language']);
		}

		return this;
	}

	/**
	 * Prepare company data for api
	 *
	 * @param data
	 */
	public getDataForApi(data: any = this): any {
		// Clone data
		let apiData: any = User.map(JSON.parse(JSON.stringify(data)));

		// Process properties
		for (const [key, value] of Object.entries(apiData)) {
			// Check for own getDataForApi method
			if (value && (<any>value).getDataForApi) {
				apiData[key] = (<any>value).getDataForApi();
			} else {
				apiData[key] = JSON.parse(JSON.stringify(value));
			}
		}

		// Reduce api data to original properties of data
		apiData = ModelHelper.intersection(JSON.parse(JSON.stringify(data)), apiData);

		// Convert language property => use code only
		if (data.language !== undefined) {
			apiData.language = {
				code: data.language
			};
		}

		// Remove local model data
		delete apiData.avatarLink;
		delete apiData.signatureLink;

		// Return data for API
		return apiData;
	}

	/**
	 * Getter for user roles (names of all roles)
	 *
	 * @return - Array with all names of the roles of the user
	 */
	public getUserRoles(): UserRole[] {
		// Get all user roles
		return this.roles ? this.roles.map((role) => role.name) : [];
	}

	/**
	 * Getter for user workflows (token of all workflows in all roles)
	 *
	 * @return - Array with all tokens of all workflows in all roles
	 */
	public getWorkflowTokens(): WorkflowToken[] {
		const roles = this.roles || [];
		// Concat all workflow token arrays
		return [].concat(
			// Destructing the array of workflow token arrays
			...roles.map(
				// Get array of workflow token arrays
				(role: any) => _.get(role, 'workflows', []).map((workflow) => workflow.token)
			)
		);
	}

	/**
	 * Getter for user companyType
	 *
	 * @return - Type of Company which the user is associated
	 */
	public getUserCompanyType(): CompanyType {
		return this.company.type;
	}

	/**
	 * User has role
	 *
	 * @param userRole user role
	 */
	public hasRole(userRole: UserRole) {
		return this.isAuthorized({ roles: userRole });
	}

	/**
	 * Check if user is authorized
	 *
	 * The user has at least one of the configured user roles and one of the configured workflow token.
	 * If the roles or workflows do not exist or are empty, they are not checked and are considered valid.
	 *
	 * @param config - Configuration of allowed user roles and workflow tokens
	 * @return Whether the user is authorized or not
	 */
	public isAuthorized(config: { roles?: UserRole | UserRole[]; workflows?: WorkflowToken | WorkflowToken[] }): boolean {
		// Default config
		const defaultConfig = {
			roles: [],
			workflows: []
		};

		// Check config
		for (let [key, value] of Object.entries(config)) {
			// Null or undefined => empty array
			if (_.isNil(value)) {
				value = [];
			}

			// Not an array => array with value
			if (!Array.isArray(value)) {
				value = <any[]>[value];
			}

			// Set (optimized) value
			config[key] = value;
		}

		// Add missing properties
		config = Object.assign({}, defaultConfig, config);

		// Check if certain roles are required
		if (config.roles && config.roles.length > 0) {
			// Check if the user has at least one of the required roles
			if (_.intersection(this.getUserRoles(), config.roles).length === 0) {
				return false;
			}
		}

		// Check if certain workflows are required
		if (config.workflows && config.workflows.length > 0) {
			const workflowTokens = this.getWorkflowTokens();
			// Check if the user has at least one of the required workflows
			if (_.intersection(workflowTokens, config.workflows).length > 0) {
				return true;
			} else {
				if (config.workflows[0] === WorkflowToken.ACCOUNT_LINKED_PORTALS_EDIT) {
					// check if given workflow by user is matching with regExp
					const re = new RegExp('^Account_(\\w*)_(Dentist|Lab)_Edit$');
					for (const token of workflowTokens) {
						if (re.test(token)) {
							return true;
						}
					}
				}
				return false;
			}
		}

		// User fulfils the conditions passed in the config
		return true;
	}

	public get fullName(): string {
		return this.firstName + ' ' + this.lastName;
	}
}
